/**
 * usePalette — the kit's [data-palette] axis (calm | vivid | cb-safe), applied
 * to the document root and persisted locally. Kept out of the shared prefs
 * slice so the reusable Oxbo-Z prefs contract (and its tests) stays untouched;
 * palette is a W-local display choice.
 */
import { useCallback, useEffect, useState } from "react";

export type Palette = "calm" | "vivid" | "cb-safe";

const PALETTES: readonly Palette[] = ["calm", "vivid", "cb-safe"] as const;
const KEY = "oxbo.palette";
const DEFAULT_PALETTE: Palette = "calm";

function isPalette(v: unknown): v is Palette {
  return typeof v === "string" && (PALETTES as readonly string[]).includes(v);
}

function load(): Palette {
  try {
    const v = typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null;
    return isPalette(v) ? v : DEFAULT_PALETTE;
  } catch {
    return DEFAULT_PALETTE;
  }
}

export function usePalette(): { palette: Palette; palettes: readonly Palette[]; setPalette: (p: Palette) => void } {
  const [palette, setPaletteState] = useState<Palette>(load);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-palette", palette);
    }
  }, [palette]);

  const setPalette = useCallback((p: Palette) => {
    setPaletteState(p);
    try {
      if (typeof localStorage !== "undefined") localStorage.setItem(KEY, p);
    } catch {
      /* storage disabled — degrade to in-memory */
    }
  }, []);

  return { palette, palettes: PALETTES, setPalette };
}
